import React, { useEffect, useMemo, useState } from 'react';
import { useApp } from '../context/AppContext';
import { betaFeatures, betaSignup, betaMe, betaFeedback, betaAdminSignups, betaAdminFeedback, betaAdminUpdateSignup } from '../lib/api';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Bug, FlaskConical, Rocket, Sparkles, Send, Loader2, Check, Users, MessageSquare, Star, Smartphone, Laptop, Monitor, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

const STAGE_COLOR = { alpha: '#ff6b8a', beta: '#22d3ee', waitlist: '#ffcf52' };

export default function Beta() {
  const { user } = useApp();
  const [features, setFeatures] = useState([]);
  const [me, setMe] = useState(null);

  useEffect(() => {
    betaFeatures().then(setFeatures).catch(() => {});
    if (user) betaMe().then(setMe).catch(() => {});
  }, [user]);

  const isAdmin = user?.role === 'admin' || user?.role === 'owner';

  return (
    <div data-testid="beta-page" className="max-w-[1240px] mx-auto">
      <header className="mb-8">
        <div className="flex items-center gap-2 text-[#22d3ee]"><FlaskConical className="w-5 h-5" /><span className="uppercase text-[11px] tracking-widest font-bold">Beta program</span></div>
        <h1 className="font-display text-[36px] md:text-[54px] mt-2 leading-[1.02]">Help us <span className="brand-gradient">forge</span> what's next.</h1>
        <p className="text-white/65 text-[15px] mt-3 max-w-[760px]">Get first access to watch parties, AI studio, tip-to-screen and more. In exchange — tell us what's broken, missing, or brilliant. Your feedback directly shapes the roadmap.</p>
        {me?.signup && (
          <div className="mt-5 card-shell p-4 inline-flex items-center gap-3" data-testid="beta-status-card">
            <ShieldCheck className="w-5 h-5 text-[#22d3ee]" />
            <div>
              <div className="text-[13px] text-white/55">Your beta status</div>
              <div className="font-display text-[18px] capitalize">{me.signup.status}</div>
            </div>
          </div>
        )}
      </header>

      <Tabs defaultValue="features" className="mt-6">
        <TabsList className="bg-white/[0.04] border border-white/10 flex-wrap h-auto">
          <TabsTrigger value="features" data-testid="beta-tab-features"><Rocket className="w-4 h-4 mr-1.5" /> Beta Features</TabsTrigger>
          <TabsTrigger value="signup" data-testid="beta-tab-signup"><Users className="w-4 h-4 mr-1.5" /> Join / My Profile</TabsTrigger>
          <TabsTrigger value="feedback" data-testid="beta-tab-feedback"><MessageSquare className="w-4 h-4 mr-1.5" /> Feedback &amp; Bugs</TabsTrigger>
          {isAdmin && <TabsTrigger value="admin" data-testid="beta-tab-admin"><ShieldCheck className="w-4 h-4 mr-1.5" /> Admin</TabsTrigger>}
        </TabsList>

        <TabsContent value="features" className="mt-6"><FeaturesTab features={features} /></TabsContent>
        <TabsContent value="signup" className="mt-6"><SignupTab features={features} me={me} onUpdate={() => betaMe().then(setMe).catch(() => {})} /></TabsContent>
        <TabsContent value="feedback" className="mt-6"><FeedbackTab features={features} me={me} onUpdate={() => betaMe().then(setMe).catch(() => {})} /></TabsContent>
        {isAdmin && <TabsContent value="admin" className="mt-6"><AdminTab /></TabsContent>}
      </Tabs>
    </div>
  );
}

function FeaturesTab({ features }) {
  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="beta-features-grid">
      {features.map(f => (
        <div key={f.id} className="card-shell p-5 hover-lift" data-testid={`beta-feature-${f.id}`}>
          <div className="flex items-center gap-2">
            <span className="chip uppercase text-[10px]" style={{ color: STAGE_COLOR[f.stage] || '#fff', borderColor: (STAGE_COLOR[f.stage] || '#fff') + '55' }}>{f.stage}</span>
            <Sparkles className="w-4 h-4 text-white/30" />
          </div>
          <div className="font-display text-[20px] mt-3">{f.name}</div>
          <p className="text-white/70 text-[13px] mt-2 leading-relaxed">{f.summary}</p>
        </div>
      ))}
      {features.length === 0 && <div className="text-white/55 py-10 col-span-full">Loading features…</div>}
    </div>
  );
}

function SignupTab({ features, me, onUpdate }) {
  const { user } = useApp();
  const signup = me?.signup;
  const [email, setEmail] = useState(user?.email || signup?.email || '');
  const [name, setName] = useState(user?.name || signup?.name || '');
  const [role, setRole] = useState(signup?.role || 'creator');
  const [device, setDevice] = useState(signup?.device || 'desktop');
  const [interests, setInterests] = useState(signup?.interests || []);
  const [notes, setNotes] = useState(signup?.notes || '');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!signup) return;
    setEmail(signup.email || email);
    setName(signup.name || name);
    setRole(signup.role || 'creator');
    setDevice(signup.device || 'desktop');
    setInterests(signup.interests || []);
    setNotes(signup.notes || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signup?.id]);

  const toggle = (id) => setInterests(list => list.includes(id) ? list.filter(x => x !== id) : [...list, id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return toast.error('Email required');
    setBusy(true);
    try {
      await betaSignup({ email: email.trim(), name: name.trim(), role, device, interests, notes: notes.trim() });
      toast.success(signup ? 'Beta profile updated' : 'You\'re on the list — watch your inbox');
      onUpdate?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Signup failed');
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="grid lg:grid-cols-[1fr_360px] gap-6" data-testid="beta-signup-form">
      <div className="card-shell p-6 space-y-4">
        <div>
          <div className="text-[12px] uppercase tracking-wider text-white/45 mb-1">Contact</div>
          <div className="grid sm:grid-cols-2 gap-3">
            <input value={email} onChange={e => setEmail(e.target.value)} placeholder="you@domain.com" className="input-field" required type="email" data-testid="beta-email" />
            <input value={name} onChange={e => setName(e.target.value)} placeholder="Display name (optional)" className="input-field" data-testid="beta-name" />
          </div>
        </div>

        <div>
          <div className="text-[12px] uppercase tracking-wider text-white/45 mb-2">I mostly use VidCreForge as a…</div>
          <div className="flex flex-wrap gap-2">
            {['creator', 'musician', 'viewer', 'advertiser', 'other'].map(r => (
              <button type="button" key={r} onClick={() => setRole(r)} className={`chip capitalize ${role === r ? 'active' : ''}`} data-testid={`beta-role-${r}`}>{r}</button>
            ))}
          </div>
        </div>

        <div>
          <div className="text-[12px] uppercase tracking-wider text-white/45 mb-2">Primary device</div>
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={() => setDevice('ios')} className={`chip ${device === 'ios' ? 'active' : ''}`}><Smartphone className="w-3.5 h-3.5 mr-1 inline" /> iOS</button>
            <button type="button" onClick={() => setDevice('android')} className={`chip ${device === 'android' ? 'active' : ''}`}><Smartphone className="w-3.5 h-3.5 mr-1 inline" /> Android</button>
            <button type="button" onClick={() => setDevice('desktop')} className={`chip ${device === 'desktop' ? 'active' : ''}`}><Laptop className="w-3.5 h-3.5 mr-1 inline" /> Desktop</button>
            <button type="button" onClick={() => setDevice('tablet')} className={`chip ${device === 'tablet' ? 'active' : ''}`}><Monitor className="w-3.5 h-3.5 mr-1 inline" /> Tablet</button>
          </div>
        </div>

        <div>
          <div className="text-[12px] uppercase tracking-wider text-white/45 mb-2">Which betas do you want in on?</div>
          <div className="grid sm:grid-cols-2 gap-2">
            {features.map(f => {
              const on = interests.includes(f.id);
              return (
                <button type="button" key={f.id} onClick={() => toggle(f.id)} className={`text-left card-shell p-3 hover-lift flex items-start gap-2 ${on ? 'ring-1 ring-[#22d3ee]/50' : ''}`} data-testid={`beta-interest-${f.id}`}>
                  <div className={`w-5 h-5 rounded-md grid place-items-center mt-0.5 shrink-0 ${on ? 'bg-[#22d3ee] text-black' : 'border border-white/25'}`}>{on && <Check className="w-3.5 h-3.5" />}</div>
                  <div className="min-w-0">
                    <div className="font-semibold text-[13.5px]">{f.name}</div>
                    <div className="text-[12px] text-white/55 truncate">{f.summary}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <div className="text-[12px] uppercase tracking-wider text-white/45 mb-1">Anything else? (optional)</div>
          <textarea rows={3} value={notes} onChange={e => setNotes(e.target.value)} placeholder="What would make VidCreForge the platform you stay on?" className="input-field resize-none" data-testid="beta-notes" />
        </div>

        <div className="flex items-center justify-end gap-2">
          <button type="submit" disabled={busy} className="btn-primary disabled:opacity-50" data-testid="beta-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />} {signup ? 'Update profile' : 'Join the beta'}
          </button>
        </div>
      </div>

      <aside className="card-shell p-5 h-fit">
        <div className="flex items-center gap-2 text-[#22d3ee]"><ShieldCheck className="w-4 h-4" /><span className="uppercase text-[11px] tracking-widest font-bold">How this works</span></div>
        <ul className="mt-3 space-y-2.5 text-[13px] text-white/75">
          <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[#22d3ee] mt-0.5 shrink-0" /> Sign up in 20 seconds. We enable your flags server-side.</li>
          <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[#22d3ee] mt-0.5 shrink-0" /> We read your device + interests so you only see betas that matter to you.</li>
          <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[#22d3ee] mt-0.5 shrink-0" /> You give feedback from the Feedback tab — bugs, ideas, screenshots.</li>
          <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[#22d3ee] mt-0.5 shrink-0" /> Active beta testers get lifetime <span className="brand-gradient font-semibold">VidCreForge Plus</span> when we graduate the feature.</li>
        </ul>
      </aside>
    </form>
  );
}

function FeedbackTab({ features, me, onUpdate }) {
  const [feature_id, setFeature] = useState('');
  const [kind, setKind] = useState('feedback');
  const [rating, setRating] = useState(0);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const mine = me?.feedback || [];

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !body.trim()) return toast.error('Title and details required');
    setBusy(true);
    try {
      await betaFeedback({ feature_id: feature_id || null, kind, rating: rating || null, title, body });
      toast.success('Thanks — logged.');
      setTitle(''); setBody(''); setRating(0); setFeature(''); setKind('feedback');
      onUpdate?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="grid lg:grid-cols-[1fr_380px] gap-6" data-testid="beta-feedback-tab">
      <form onSubmit={submit} className="card-shell p-6 space-y-4">
        <div className="flex flex-wrap gap-2">
          {[
            { id: 'feedback', label: 'Feedback', icon: MessageSquare },
            { id: 'bug',      label: 'Bug',      icon: Bug },
            { id: 'idea',     label: 'Idea',     icon: Sparkles },
          ].map(k => (
            <button type="button" key={k.id} onClick={() => setKind(k.id)} className={`chip ${kind === k.id ? 'active' : ''}`} data-testid={`beta-kind-${k.id}`}>
              <k.icon className="w-3.5 h-3.5 mr-1 inline" /> {k.label}
            </button>
          ))}
        </div>

        <div>
          <div className="text-[12px] uppercase tracking-wider text-white/45 mb-1">Which feature? (optional)</div>
          <select value={feature_id} onChange={e => setFeature(e.target.value)} className="input-field" data-testid="beta-feedback-feature">
            <option value="">— Any / platform-wide —</option>
            {features.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
          </select>
        </div>

        <div>
          <div className="text-[12px] uppercase tracking-wider text-white/45 mb-1">Rating (optional)</div>
          <div className="flex items-center gap-1">
            {[1,2,3,4,5].map(n => (
              <button type="button" key={n} onClick={() => setRating(n === rating ? 0 : n)} className="p-1" aria-label={`Rate ${n}`} data-testid={`beta-star-${n}`}>
                <Star className={`w-6 h-6 ${n <= rating ? 'fill-[#ffcf52] text-[#ffcf52]' : 'text-white/30'}`} />
              </button>
            ))}
          </div>
        </div>

        <input value={title} onChange={e => setTitle(e.target.value)} placeholder="One-line summary" className="input-field" maxLength={140} data-testid="beta-feedback-title" />
        <textarea rows={5} value={body} onChange={e => setBody(e.target.value)} placeholder="Steps to reproduce / why it matters / screenshots welcome in the notes" className="input-field resize-none" maxLength={4000} data-testid="beta-feedback-body" />

        <div className="flex justify-end">
          <button type="submit" disabled={busy} className="btn-primary disabled:opacity-50" data-testid="beta-feedback-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send
          </button>
        </div>
      </form>

      <aside className="card-shell p-5">
        <div className="flex items-center gap-2 text-white/75"><MessageSquare className="w-4 h-4" /><span className="uppercase text-[11px] tracking-widest font-bold">Your feedback</span></div>
        <div className="mt-3 space-y-3 max-h-[480px] overflow-y-auto">
          {mine.length === 0 && <div className="text-[13px] text-white/55">You haven't sent anything yet.</div>}
          {mine.map(f => (
            <div key={f.id} className="border border-white/10 rounded-lg p-3" data-testid={`beta-feedback-row-${f.id}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="text-[13px] font-semibold truncate">{f.title}</div>
                <span className="chip text-[10px] uppercase">{f.kind}</span>
              </div>
              <div className="text-[12px] text-white/55 mt-0.5">{new Date(f.created_at).toLocaleString()}</div>
              <div className="text-[13px] text-white/75 mt-1 line-clamp-3 whitespace-pre-wrap">{f.body}</div>
            </div>
          ))}
        </div>
      </aside>
    </div>
  );
}

function AdminTab() {
  const [signups, setSignups] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [s, f] = await Promise.all([betaAdminSignups(), betaAdminFeedback()]);
      setSignups(s); setFeedback(f);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const update = async (id, status) => {
    try { await betaAdminUpdateSignup(id, status); toast.success('Updated'); load(); }
    catch { toast.error('Failed'); }
  };

  const stats = useMemo(() => ({
    total: signups.length,
    approved: signups.filter(s => s.status === 'approved').length,
    pending: signups.filter(s => s.status === 'pending').length,
    feedback: feedback.length,
  }), [signups, feedback]);

  if (loading) return <div className="flex items-center gap-2 text-white/60 py-10"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;

  return (
    <div className="space-y-6" data-testid="beta-admin-tab">
      <div className="grid sm:grid-cols-4 gap-3">
        {[
          { label: 'Signups',   v: stats.total },
          { label: 'Approved',  v: stats.approved },
          { label: 'Pending',   v: stats.pending },
          { label: 'Feedback',  v: stats.feedback },
        ].map(s => (
          <div key={s.label} className="card-shell p-4">
            <div className="text-[11px] uppercase tracking-wider text-white/45">{s.label}</div>
            <div className="font-display text-[28px]">{s.v}</div>
          </div>
        ))}
      </div>

      <div className="card-shell overflow-hidden">
        <div className="px-4 py-3 text-[12px] uppercase tracking-wider text-white/55">Signups</div>
        <table className="w-full text-[13px]">
          <thead className="bg-white/[0.03] text-white/55">
            <tr><th className="text-left px-4 py-2">Email</th><th className="text-left px-3 py-2">Role</th><th className="text-left px-3 py-2">Device</th><th className="text-left px-3 py-2">Interests</th><th className="text-left px-3 py-2">Status</th><th className="px-4 py-2"></th></tr>
          </thead>
          <tbody>
            {signups.map(s => (
              <tr key={s.id} className="border-t border-white/5" data-testid={`beta-admin-row-${s.id}`}>
                <td className="px-4 py-3"><div className="font-semibold">{s.email}</div><div className="text-[11.5px] text-white/50">{s.name || '—'}</div></td>
                <td className="px-3 py-3 capitalize">{s.role}</td>
                <td className="px-3 py-3 capitalize">{s.device}</td>
                <td className="px-3 py-3 text-white/70 text-[12px]">{(s.interests || []).join(', ') || '—'}</td>
                <td className="px-3 py-3"><span className={`chip capitalize ${s.status === 'approved' ? 'active' : ''}`}>{s.status}</span></td>
                <td className="px-3 py-3 text-right whitespace-nowrap">
                  {s.status !== 'approved' && <button onClick={() => update(s.id, 'approved')} className="btn-ghost text-[12px] mr-1" data-testid={`beta-admin-approve-${s.id}`}>Approve</button>}
                  {s.status !== 'rejected' && <button onClick={() => update(s.id, 'rejected')} className="btn-ghost text-[12px]">Reject</button>}
                </td>
              </tr>
            ))}
            {signups.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-white/55">No signups yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="card-shell overflow-hidden">
        <div className="px-4 py-3 text-[12px] uppercase tracking-wider text-white/55">Feedback</div>
        <div className="divide-y divide-white/5">
          {feedback.map(f => (
            <div key={f.id} className="px-4 py-3" data-testid={`beta-admin-feedback-${f.id}`}>
              <div className="flex items-center gap-2">
                <span className="chip uppercase text-[10px]">{f.kind}</span>
                {f.rating && <span className="text-[11.5px] text-[#ffcf52]">{'★'.repeat(f.rating)}</span>}
                <span className="text-[12px] text-white/55">{f.email}</span>
                <span className="text-[12px] text-white/40 ml-auto">{new Date(f.created_at).toLocaleString()}</span>
              </div>
              <div className="font-semibold mt-1">{f.title}</div>
              <div className="text-white/75 text-[13px] mt-0.5 whitespace-pre-wrap">{f.body}</div>
            </div>
          ))}
          {feedback.length === 0 && <div className="px-4 py-10 text-center text-white/55">No feedback yet.</div>}
        </div>
      </div>
    </div>
  );
}
